<script>
import { ref } from 'vue';
import _ from 'lodash';
import { useRouter } from 'vue-router';

import { usePipelinesStore } from '../../stores/pipelines.store';
import { useTagsStore } from '@/stores/tags.store';
import { useMetricsStore } from '../../stores/metrics.store';
import { useSourcesStore } from '../../stores/sources.store';
import { useDestinationsStore } from '../../stores/destinations.store';

import { getStatusColor, getBadgeVariantAndClasses, formatter, userHasAccess, getGlobalConfigValue } from '../../utils/utils';
import { getConnectorIcon } from '@/utils/constants';
import { SkeletonLoader } from '@/components/ui/skeleton-loader';
import { DotSpinner } from '@/components/ui/dot-spinner';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableEmpty } from '@/components/ui/table';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import TablePagination from '@/components/TablePagination.vue';

import { Clock, Info } from 'lucide-vue-next';
import { ChevronDown, ChevronUp, ChevronsUpDown, PlusCircle, Check, Tag } from 'lucide-vue-next';
import { useDeleteConfirmation, useConfirmDialog } from '@/composables/useDialog';
import ActionMenu from '@/components/ActionMenu.vue';
import { TableEmptyState } from '@/components/ui/table-empty-state';
import { useErrorDialog } from '@/composables/useDialog';
import StatusFilter from '@/components/filters/StatusFilter.vue';
import EntityFilter from '@/components/filters/EntityFilter.vue';
import GroupedConnectorFilter from '@/components/filters/GroupedConnectorFilter.vue';
import { toast } from '@/composables/useToast';
import SearchInput from '@/components/global/SearchInput.vue';
import { useAuthStore } from '@/stores/auth.store';
import {
    NO_SNAPSHOT_CONNECTORS,
    TAG_BADGE_COLOR_CLASSES_PER_TYPE,
    TAG_COLORS_PER_NAME,
    TAG_TYPES,
    TagTypeToDisplayName,
    Permissions
} from '@/utils/constants';
import { useLoaderStore } from '@/stores/loader.store';
import BulkUpdateTagsOnPipelinesModal from '@/components/Tags/BulkUpdateTagsOnPipelinesModal.vue';
import WebSocketService from '../../services/webSocket.service';
import MultiselectWithOperator from '@/components/MultiselectWithOperator.vue';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export default {
    name: 'PipelinesPage',
    components: {
        Info,
        ChevronDown,
        ChevronUp,
        ChevronsUpDown,
        Clock,
        PlusCircle,
        Check,
        Badge,
        Avatar,
        AvatarImage,
        AvatarFallback,
        SkeletonLoader,
        MultiselectWithOperator,
        Tag,
        ActionMenu,
        SearchInput,
        BulkUpdateTagsOnPipelinesModal,
        Button,
        Tooltip,
        TooltipContent,
        TooltipProvider,
        TooltipTrigger,
        StatusFilter,
        EntityFilter,
        GroupedConnectorFilter,
        Table,
        TableBody,
        TableCell,
        TableHead,
        TableHeader,
        TableRow,
        TableEmpty,
        TablePagination,
        Checkbox,
        ScrollArea,
        ScrollBar,
        DotSpinner,
        TableEmptyState
    },
    data() {
        return {
            metricsStore: useMetricsStore(),
            router: useRouter(),
            authStore: useAuthStore(),
            sourcesStore: useSourcesStore(),
            loaderStore: useLoaderStore(),
            pipelinesStore: usePipelinesStore(),
            tagsStore: useTagsStore(),
            destinationsStore: useDestinationsStore(),
            itemLoading: ref({}),
            searchQuery: null,
            sortColumn: ref('source.name'),
            sortDirection: ref(false),
            generalLoading: ref(true),
            actionLoading: ref({}),
            metricsLoading: ref(false),
            NO_SNAPSHOT_CONNECTORS,
            TAG_TYPES,
            page: 0,
            pageSize: 0,
            maxTagsToShow: 4,
            statusFilter: [],
            sourceFilter: [],
            destinationFilter: [],
            groupedConnectorFilter: { sources: [], destinations: [] },
            TAG_BADGE_COLOR_CLASSES_PER_TYPE,
            TAG_COLORS_PER_NAME,
            showMoreTags: {},
            tagFilter: {
                selected: [],
                operator: 'OR'
            },
            checkedPipeline: {},
            allPipelinesAreChecked: false,
            isBulkUpdateTagsModalOpen: false,
            pipelineInModalView: null,
            TagTypeToDisplayName
        };
    },
    computed: {
        allLatestMetrics() {
            return this.metricsStore.pipelines.allLatest;
        },
        pipelineTags() {
            return this.tagsStore.tagsPerTypeForEntityCRUD[this.TAG_TYPES.PIPELINE] || [];
        },
        searchedPipelines() {
            let result = this.applyFilters(this.pipelinesStore.all);
            result = this.applySorting(result);

            return result;
        },
        statusOptions() {
            return ['Active', 'Broken', 'Delayed', 'Paused'];
        },
        sourceOptions() {
            const sources = [];
            // Use unfiltered pipelines to avoid circular dependency
            this.pipelinesStore.all?.forEach((pipeline) => {
                if (!sources.find((s) => s.name === pipeline.source.name)) {
                    sources.push({
                        id: pipeline.source.id,
                        name: pipeline.source.name,
                        value: pipeline.source.name
                    });
                }
            });
            return sources;
        },
        destinationOptions() {
            const destinations = [];
            // Use unfiltered pipelines to avoid circular dependency
            this.pipelinesStore.all?.forEach((pipeline) => {
                if (!destinations.find((d) => d.name === pipeline.destination.name)) {
                    destinations.push({
                        id: pipeline.destination.id,
                        name: pipeline.destination.name,
                        value: pipeline.destination.name
                    });
                }
            });
            return destinations;
        },
        anyCheckedPipeline() {
            return _.some(Object.values(this.checkedPipeline), (value) => value);
        },
        maxNumberOfTagsPerPipeline() {
            return this.searchedPipelines?.reduce((acc, pipeline) => {
                const tags = this.allPipelineTags(pipeline);
                return Math.max(acc, tags.length);
            }, 0);
        },
        canPipelineSnapshot() {
            return (pipeline) => {
                const connectorConfig = this.sourcesStore.configurations[pipeline.source.connector];
                const snapshotDisabled = getGlobalConfigValue(connectorConfig, 'incremental.snapshot.disabled');
                const result = snapshotDisabled !== true;

                return result;
            };
        },
        canPipelineCancelSnapshot() {
            return (pipeline) => {
                const connectorConfig = this.sourcesStore.configurations[pipeline.source.connector];
                const snapshotDisabled = getGlobalConfigValue(connectorConfig, 'incremental.snapshot.disabled');
                const result = snapshotDisabled !== true;

                return result;
            };
        }
    },
    watch: {
        generalLoading: {
            handler: function (newValue) {
                if (newValue === true) {
                    // Scroll to top of the main content area
                    const mainElement = document.querySelector('main');
                    if (mainElement) {
                        mainElement.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                }
            }
        },
        searchQuery: {
            handler: _.debounce(async function () {
                await this.loadPipelines();
            }, 700)
        }
    },
    mounted() {
        // Reset pagination to first page on mount to prevent persisting page state across navigation
        this.page = 0;
        this.pageSize = this.pipelinesStore.pageSize || 10;

        this.loadPipelines();
        // Set default page size

        // Use Vue lifecycle instead of document visibility handlers
        this.addWebsocketListeners();
    },
    unmounted() {
        this.removeWebsocketListeners();
    },
    methods: {
        userHasAccess,
        formatter,
        getConnectorIcon,
        getFormattedValue(pipelineToSearch, metricKey, unit, dashOnRule = null) {
            const metricValue = this.allLatestMetrics[pipelineToSearch.id]?.inline?.[metricKey];

            const formatterParams = [metricValue, { unit: unit || null }];

            if (dashOnRule || dashOnRule === false) {
                formatterParams.push(dashOnRule);
            }

            return this.formatter(...formatterParams);
        },
        getStatusColor,
        getBadgeVariantAndClasses,
        showStopSource(pipeline) {
            return (
                pipeline?.source.connector !== 'kafkadirect' &&
                !(
                    pipeline.source.connector_status?.toLowerCase() === 'paused' ||
                    pipeline.source.connector_status?.toLowerCase() === 'starting' ||
                    pipeline.source.connector_status?.toLowerCase() === 'stopped'
                )
            );
        },
        showResumeSource(pipeline) {
            return (
                pipeline?.source.connector !== 'kafkadirect' &&
                !(
                    pipeline.source.connector_status?.toLowerCase() === 'active' ||
                    pipeline.source.connector_status?.toLowerCase() === 'broken' ||
                    pipeline.source.connector_status?.toLowerCase() === 'starting'
                )
            );
        },
        showStopDestination(pipeline) {
            return (
                pipeline?.destination.connector !== 'kafkadirect' &&
                !(
                    pipeline.destination.connector_status?.toLowerCase() === 'paused' ||
                    pipeline.destination.connector_status?.toLowerCase() === 'starting' ||
                    pipeline.destination.connector_status?.toLowerCase() === 'stopped'
                )
            );
        },
        showResumeDestination(pipeline) {
            return (
                pipeline?.destination.connector !== 'kafkadirect' &&
                !(
                    pipeline.destination.connector_status?.toLowerCase() === 'active' ||
                    pipeline.destination.connector_status?.toLowerCase() === 'broken' ||
                    pipeline.destination.connector_status?.toLowerCase() === 'starting'
                )
            );
        },
        onCreatePipeline() {
            return this.router.push({ name: 'new-pipeline' });
        },
        onPipelineSelected(pipeline) {
            return this.router.push(this.selectedPipelineRouterPath(pipeline));
        },
        selectedPipelineRouterPath(pipeline) {
            return {
                name: 'pipeline-details',
                params: {
                    id: pipeline.id
                },
                query: {
                    source: pipeline.source.id,
                    destination: pipeline.destination.id
                }
            };
        },
        selectedConnectorPath(id, type = 'source') {
            return type === 'source' ? { name: 'source-details', params: { id } } : { name: 'destination-details', params: { id } };
        },
        async onDelete(entity, name, id) {
            this.actionLoading[id] = true;

            try {
                await useDeleteConfirmation({
                    entity,
                    name,
                    title: `Delete ${entity}`,
                    message: `Are you sure you want to delete the ${entity} <strong>"${name}"</strong>? This action cannot be undone.`,
                    onConfirm: async () => {
                        await this.pipelinesStore.delete(id);
                    }
                });
            } catch (error) {
                // User cancelled or error occurred
                if (error.message) {
                    console.error('Delete failed:', error);
                }
            } finally {
                this.actionLoading[id] = false;
            }
        },
        onPipelineViewed(id) {
            return this.router.push({ name: 'pipeline-details', params: { id } });
        },
        // async onPauseDestination(pipeline) {
        //     this.actionLoading[pipeline.id] = true;
        //     try {
        //         await this.destinationsStore.pause(pipeline.destination.id, true);
        //         this.actionLoading[pipeline.id] = false;
        //         toast.success('Destination paused successfully.');
        //         this.itemLoading[pipeline.id] = true;
        //         await this.metricsStore.pipelines.fetchInlinePipelineMetrics([pipeline.id], true);
        //         this.itemLoading[pipeline.id] = false;
        //     } catch (e) {
        //         toast.error('There was an error while pausing the destination. Please try again.');
        //     } finally {
        //         this.actionLoading[pipeline.id] = false;
        //         this.itemLoading[pipeline.id] = false;
        //     }
        // },
        async onStopDestination(pipeline) {
            this.actionLoading[pipeline.id] = true;
            try {
                await this.destinationsStore.stop(pipeline.destination.id, true);
                this.actionLoading[pipeline.id] = false;
                toast.success('Destination stopped successfully.');
                this.itemLoading[pipeline.id] = true;
                await this.metricsStore.pipelines.fetchInlinePipelineMetrics([pipeline.id], true);
                this.itemLoading[pipeline.id] = false;
            } catch (e) {
                toast.error('There was an error while stopping the destination. Please try again.');
            } finally {
                this.actionLoading[pipeline.id] = false;
                this.itemLoading[pipeline.id] = false;
            }
        },
        async onResumeDestination(pipeline) {
            this.actionLoading[pipeline.id] = true;
            try {
                await this.destinationsStore.resume(pipeline.destination.id, true);
                this.actionLoading[pipeline.id] = false;
                toast.success('Destination resumed successfully.');
                this.itemLoading[pipeline.id] = true;
                await this.metricsStore.pipelines.fetchInlinePipelineMetrics([pipeline.id], true);
                this.itemLoading[pipeline.id] = false;
            } catch (e) {
                toast.error('There was an error while resuming the destination. Please try again.');
            } finally {
                this.actionLoading[pipeline.id] = false;
                this.itemLoading[pipeline.id] = false;
            }
        },
        // async onPauseSource(pipeline) {
        //     this.actionLoading[pipeline.id] = true;
        //     try {
        //         await this.sourcesStore.pause(pipeline.source.id, true);
        //         this.actionLoading[pipeline.id] = false;

        //         toast.success('Source paused successfully.');
        //         this.itemLoading[pipeline.id] = true;
        //         await this.metricsStore.pipelines.fetchInlinePipelineMetrics([pipeline.id], true);
        //         this.itemLoading[pipeline.id] = false;
        //     } catch (e) {
        //         toast.error('There was an error while pausing the source. Please try again.');
        //     } finally {
        //         this.actionLoading[pipeline.id] = false;
        //         this.itemLoading[pipeline.id] = false;
        //     }
        // },
        async onStopSource(pipeline) {
            this.actionLoading[pipeline.id] = true;
            try {
                await this.sourcesStore.stop(pipeline.source.id, true);
                this.actionLoading[pipeline.id] = false;

                toast.success('Source stopped successfully.');
                this.itemLoading[pipeline.id] = true;
                await this.metricsStore.pipelines.fetchInlinePipelineMetrics([pipeline.id], true);
                this.itemLoading[pipeline.id] = false;
            } catch (e) {
                toast.error('There was an error while stopping the source. Please try again.');
            } finally {
                this.actionLoading[pipeline.id] = false;
                this.itemLoading[pipeline.id] = false;
            }
        },
        async onResumeSource(pipeline) {
            this.actionLoading[pipeline.id] = true;
            try {
                await this.sourcesStore.resume(pipeline.source.id, true);
                this.actionLoading[pipeline.id] = false;

                this.itemLoading[pipeline.id] = true;
                await this.metricsStore.pipelines.fetchInlinePipelineMetrics([pipeline.id], true);
                this.itemLoading[pipeline.id] = false;
                toast.success('Source resumed successfully.');
            } catch (e) {
                toast.error('There was an error while resuming the source. Please try again.');
            } finally {
                this.actionLoading[pipeline.id] = false;
                this.itemLoading[pipeline.id] = false;
            }
        },
        async onUpdateTags(pipeline) {
            this.pipelineInModalView = pipeline;
            this.isBulkUpdateTagsModalOpen = true;
        },
        onViewLogs(pipeline) {
            const route = this.router.resolve({
                name: 'logs',
                query: { source_id: pipeline.source.id, destination_id: pipeline.destination.id }
            });
            window.open(route.href, '_blank');
        },
        async onCopyId(id) {
            try {
                await navigator.clipboard.writeText(id);
                toast.success('Copied!', {
                    description: `ID "${id}" copied to clipboard`
                });
            } catch (error) {
                console.error('Failed to copy ID:', error);
                toast.error('Failed to copy ID to clipboard');
            }
        },
        async loadPipelines() {
            try {
                this.generalLoading = true;

                if (this.searchQuery || this.searchQuery === '') {
                    this.page = 0;
                }

                await Promise.all([
                    this.tagsStore.fetch(),
                    this.pipelinesStore.fetchCreated(this.page, this.pageSize, this.searchQuery, this.tagFilter.selected, this.tagFilter.operator)
                ]);

                const pipelineIds = this.pipelinesStore.all?.map((pipeline) => pipeline.id);

                this.generalLoading = false;
                this.metricsLoading = true;
                await this.metricsStore.pipelines.fetchInlinePipelineMetrics(pipelineIds);
            } finally {
                this.generalLoading = false;
                this.metricsLoading = false;
            }
        },
        swapSorting(column, direction) {
            this.sortColumn = column;
            this.sortDirection = direction;
        },
        applySorting(result) {
            return _.orderBy(result, [this.sortColumn], [this.sortDirection ? 'desc' : 'asc']);
        },
        applyFilters(result) {
            return result?.filter(
                (x) =>
                    (this.statusFilter?.length <= 0 || this.statusFilter?.includes(this?.allLatestMetrics[x?.id]?.inline?.status)) &&
                    (this.sourceFilter?.length <= 0 || this.sourceFilter?.includes(x?.source?.id)) &&
                    (this.destinationFilter?.length <= 0 || this.destinationFilter?.includes(x?.destination?.id))
            );
        },
        onStatusFilterChange(values) {
            this.statusFilter = values.includes('All') ? [] : values;
        },
        onSourceFilterChange(values) {
            // EntityFilter returns array of selected values, empty array means show all
            this.sourceFilter = values || [];
        },
        onDestinationFilterChange(values) {
            // EntityFilter returns array of selected values, empty array means show all
            this.destinationFilter = values || [];
        },
        onGroupedConnectorFilterChange(value) {
            // Update both individual filters and the grouped filter
            this.groupedConnectorFilter = value || { sources: [], destinations: [] };
            this.sourceFilter = value.sources || [];
            this.destinationFilter = value.destinations || [];
        },
        onPipelinesPerPageChange(newSize) {
            this.pageSize = newSize;
            this.page = 0;
            this.loadPipelines();
        },
        onPageChange(newPage) {
            this.page = newPage;
            this.loadPipelines();
        },
        onStatusClick(connector) {
            if (connector.connector_status === 'Broken' && connector.task_statuses) {
                const errors = [...new Set(Object.values(connector.task_statuses).map((task) => task.trace))];
                useErrorDialog({
                    errors,
                    width: 65
                });
            }
        },
        onPipelineStatusClick(pipeline) {
            const source = pipeline.source;
            const destination = pipeline.destination;
            let sourceTaskStatuses = source.connector_status === 'Broken' ? source.task_statuses || {} : {};
            let destinationTaskStatuses = destination.connector_status === 'Broken' ? destination.task_statuses || {} : {};

            sourceTaskStatuses = new Set(
                Object.values(sourceTaskStatuses).map(
                    (task) => `<b>SOURCE - ${source.connector} (${source.name} - ID: ${source.id})</b> - ${task.trace}\n`
                )
            );
            destinationTaskStatuses = new Set(
                Object.values(destinationTaskStatuses).map(
                    (task) => `\n<b>DESTINATION - ${destination.connector} (${destination.name} - ID: ${destination.id})</b> - ${task.trace}`
                )
            );

            if (sourceTaskStatuses.size || destinationTaskStatuses.size) {
                const errors = [...sourceTaskStatuses, ...destinationTaskStatuses];
                useErrorDialog({
                    errors,
                    width: 65
                });
            }
        },
        allPipelineTags(pipeline) {
            return this.pipelineTags.filter((tag) => pipeline.tags.includes(tag.id || tag._id));
        },
        visibleTags(pipeline) {
            let filteredTags = this.pipelineTags.filter((tag) => pipeline.tags.includes(tag.id || tag._id));
            filteredTags = _.orderBy(filteredTags, [(tag) => (tag.type.includes(TAG_TYPES.ENVIRONMENT) ? 0 : 1), 'system'], ['asc', 'desc']);

            return this.showMoreTags[pipeline.id] ? filteredTags : filteredTags.slice(0, this.maxTagsToShow);
        },
        pipelineTagsToShowMore(pipeline) {
            const filteredTags = this.pipelineTags.filter((tag) => pipeline.tags.includes(tag.id || tag._id));
            return filteredTags.length > this.maxTagsToShow;
        },
        toggleShowMoreTags(pipelineId) {
            this.showMoreTags[pipelineId] = !this.showMoreTags[pipelineId];
        },
        async onSnapshot(pipeline) {
            this.actionLoading[pipeline.id] = true;

            try {
                await useConfirmDialog({
                    type: 'snapshot',
                    title: 'Trigger Snapshot',
                    message: `Are you sure you want to trigger a snapshot on all tables for <strong>"${pipeline.name}"</strong>?`,
                    confirmText: 'Trigger Snapshot',
                    requiresInput: true,
                    inputPlaceholder: 'Type "snapshot" to confirm',
                    expectedInput: 'snapshot',
                    onConfirm: async () => {
                        const snapshotBody = {
                            source_id: pipeline.source.id,
                            connector: pipeline.source.connector,
                            topic_names: pipeline.topics
                        };
                        await this.sourcesStore.snapshot(snapshotBody);
                    }
                });
            } catch (error) {
                // User cancelled or error occurred
                if (error.message) {
                    console.error('Snapshot failed:', error);
                }
            } finally {
                this.actionLoading[pipeline.id] = false;
            }
        },
        async onCancelSnapshot(pipeline) {
            this.actionLoading[pipeline.id] = true;

            try {
                const snapshotBody = {
                    source_id: pipeline.source.id,
                    connector: pipeline.source.connector,
                    topic_names: pipeline.topics
                };

                await this.sourcesStore.cancelSnapshot(snapshotBody);
            } finally {
                this.actionLoading[pipeline.id] = false;
            }
        },
        onPipelineView(id) {
            return this.router.push({ name: 'pipeline-details', params: { id } });
        },
        checkPipeline(id, checked) {
            this.checkedPipeline[id] = checked;
            const allCheckedIds = Object.keys(this.checkedPipeline).filter((x) => this.checkedPipeline[x]);
            const currentPipelines = this.searchedPipelines || [];
            const allPipelinesShownAreChecked =
                currentPipelines.length > 0 && currentPipelines.map((x) => x.id).every((x) => allCheckedIds.includes(x));
            this.allPipelinesAreChecked = allPipelinesShownAreChecked;
        },
        checkAllPipelines(checked) {
            const currentPipelines = this.searchedPipelines || [];
            currentPipelines.forEach((pipeline) => {
                this.checkPipeline(pipeline.id, checked);
            });
        },
        async handleBulkSave({ updateAll, tags }) {
            try {
                await this.pipelinesStore.bulkUpdateTags(
                    this.pipelineInModalView?.id
                        ? [this.pipelineInModalView?.id]
                        : updateAll
                          ? []
                          : Object.keys(this.checkedPipeline).filter((x) => this.checkedPipeline[x]),
                    this.pipelineInModalView?.id ? false : updateAll,
                    tags
                );

                this.isBulkUpdateTagsModalOpen = false;
                this.pipelineInModalView = null;
            } catch (error) {
                console.error('Error in doing bulk update : ', error);
            }
        },
        addWebsocketListeners() {
            WebSocketService.addListener({
                topic: 'metrics',
                entityType: 'pipelines',
                entityId: null,
                entityLevels: ['inline'],
                timeType: 'latest',
                callback: this.metricsStore.onMetricsData,
                callbackId: 'metrics.onMetricsData'
            });
        },
        removeWebsocketListeners() {
            WebSocketService.removeListener({
                topic: 'metrics',
                entityType: 'pipelines',
                entityId: null,
                entityLevels: ['inline'],
                timeType: 'latest',
                callbackId: 'metrics.onMetricsData'
            });
        }
    }
};
</script>

<template>
    <div class="mx-auto w-full">
        <BulkUpdateTagsOnPipelinesModal
            v-show="isBulkUpdateTagsModalOpen"
            :isOpen="isBulkUpdateTagsModalOpen"
            :single-pipeline="!!pipelineInModalView"
            :selected-pipeline-count="Object.values(checkedPipeline).filter((x) => x).length"
            :all-selected="allPipelinesAreChecked"
            @update:isOpen="
                (event) => {
                    isBulkUpdateTagsModalOpen = event;
                    pipelineInModalView = null;
                }
            "
            @save="handleBulkSave" />

        <div>
            <!-- Mobile Layout -->
            <div class="block md:hidden space-y-4 mb-6">
                <!-- Actions Row -->
                <div class="flex flex-col sm:flex-row gap-3">
                    <Button
                        v-can-access.permissions="[$Permissions.WRITE_PIPELINES]"
                        variant="outline"
                        class="gap-2 w-full sm:w-auto"
                        @click="onCreatePipeline">
                        <PlusCircle class="h-4 w-4" />
                        Create
                    </Button>
                    <SearchInput
                        :loading="generalLoading"
                        placeholder="Search by name..."
                        @search="(input) => (searchQuery = input)"
                        class="w-full sm:flex-1" />
                </div>

                <!-- Filters Row -->
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <!-- Moved: GroupedConnectorFilter first -->
                    <GroupedConnectorFilter
                        v-model="groupedConnectorFilter"
                        :sources="sourceOptions"
                        :destinations="destinationOptions"
                        :loading="generalLoading"
                        placeholder="Connectors"
                        class="w-full"
                        @update:model-value="onGroupedConnectorFilterChange" />

                    <StatusFilter
                        v-model="statusFilter"
                        :statuses="statusOptions"
                        :loading="generalLoading"
                        placeholder="Status"
                        :enable-filter-icon="true"
                        class="w-full"
                        @update:model-value="onStatusFilterChange" />
                </div>

                <!-- Additional Filters Row -->
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <MultiselectWithOperator
                        v-can-access.permissions="[$Permissions.READ_TAGS]"
                        :options="pipelineTags"
                        :enableOperators="true"
                        trackBy="id"
                        :disabled="generalLoading"
                        optionLabel="name"
                        placeholder="Tags"
                        manageButtonText="Manage Tags"
                        class="w-full"
                        @apply="
                            (event) => {
                                tagFilter = event;
                                loadPipelines();
                            }
                        "
                        @onManageEntity="router.push({ name: 'tags' })">
                        <template #displayTitle="{ selectedCount, placeholder }">
                            <span v-if="selectedCount">{{ selectedCount }} {{ selectedCount > 1 ? 'tags' : 'tag' }} selected</span>
                            <span v-else>{{ placeholder }}</span>
                        </template>
                        <template #option="{ option }">
                            <div class="flex items-center justify-between">
                                <div>{{ option.name }}</div>
                                <div v-if="option.type?.filter((x) => x !== TAG_TYPES.PIPELINE).length > 0" v-for="type in option.type" :key="type">
                                    <span
                                        v-if="type !== TAG_TYPES.PIPELINE"
                                        :class="`mt-0.5 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[0.6rem] font-medium ring-1 ring-inset ring-red-600/20 ${TAG_BADGE_COLOR_CLASSES_PER_TYPE[type.toLowerCase()]}`">
                                        {{ TagTypeToDisplayName[type] }}</span
                                    >
                                </div>
                            </div>
                        </template>
                    </MultiselectWithOperator>
                </div>
            </div>

            <!-- Desktop Layout -->
            <div class="hidden md:flex justify-between items-center mb-6">
                <div class="flex gap-3 items-center">
                    <Button v-can-access.permissions="[$Permissions.WRITE_PIPELINES]" variant="outline" class="gap-2" @click="onCreatePipeline">
                        <PlusCircle class="h-4 w-4" />
                        Create
                    </Button>

                    <SearchInput :loading="generalLoading" placeholder="Search by name..." @search="(input) => (searchQuery = input)" />
                </div>

                <div class="flex gap-3 items-center">
                    <!-- Moved: GroupedConnectorFilter first -->
                    <GroupedConnectorFilter
                        v-model="groupedConnectorFilter"
                        :sources="sourceOptions"
                        :destinations="destinationOptions"
                        :loading="generalLoading"
                        placeholder="Connectors"
                        @update:model-value="onGroupedConnectorFilterChange" />

                    <StatusFilter
                        v-model="statusFilter"
                        :statuses="statusOptions"
                        :loading="generalLoading"
                        placeholder="Status"
                        :enable-filter-icon="true"
                        @update:model-value="onStatusFilterChange" />

                    <MultiselectWithOperator
                        v-can-access.permissions="[$Permissions.READ_TAGS]"
                        :options="pipelineTags"
                        :enableOperators="true"
                        trackBy="id"
                        :disabled="generalLoading"
                        optionLabel="name"
                        placeholder="Tags"
                        manageButtonText="Manage Tags"
                        @apply="
                            (event) => {
                                tagFilter = event;
                                loadPipelines();
                            }
                        "
                        @onManageEntity="router.push({ name: 'tags' })">
                        <template #displayTitle="{ selectedCount, placeholder }">
                            <span v-if="selectedCount">{{ selectedCount }} {{ selectedCount > 1 ? 'tags' : 'tag' }} selected</span>
                            <span v-else>{{ placeholder }}</span>
                        </template>

                        <template #option="{ option }">
                            <div class="flex items-center justify-between">
                                <div>{{ option.name }}</div>
                                <div v-if="option.type?.filter((x) => x !== TAG_TYPES.PIPELINE).length > 0" v-for="type in option.type" :key="type">
                                    <span
                                        v-if="type !== TAG_TYPES.PIPELINE"
                                        :class="`mt-0.5 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[0.6rem] font-medium ring-1 ring-inset ring-red-600/20 ${TAG_BADGE_COLOR_CLASSES_PER_TYPE[type.toLowerCase()]}`">
                                        {{ TagTypeToDisplayName[type] }}</span
                                    >
                                </div>
                            </div>
                        </template>
                    </MultiselectWithOperator>

                    <!-- Option 2: Keep original separate filters (uncomment to use separate filters) -->
                    <!--
                    <EntityFilter
                        v-model="sourceFilter"
                        :entities="sourceOptions"
                        :loading="generalLoading"
                        placeholder="Source"
                        :enable-filter-icon="true"
                        @update:model-value="onSourceFilterChange" />

                    <EntityFilter
                        v-model="destinationFilter"
                        :entities="destinationOptions"
                        :loading="generalLoading"
                        placeholder="Destination"
                        :enable-filter-icon="true"
                        @update:model-value="onDestinationFilterChange" />
                    -->
                </div>
            </div>

            <div class="mt-2">
                <ScrollArea :class="(searchedPipelines?.length || 0) <= 10 ? 'h-auto' : 'h-70-dynamic'" class="w-full border shadow-md rounded-md">
                    <Table>
                        <TableHeader class="sticky top-0 bg-background z-10">
                            <TableRow class="bg-muted/50">
                                <TableHead v-if="searchedPipelines.length" class="first:rounded-tl-md">
                                    <div class="flex items-center gap-3">
                                        <Checkbox
                                            v-can-access.permissions="[$Permissions.WRITE_PIPELINES]"
                                            :checked="allPipelinesAreChecked"
                                            @update:modelValue="checkAllPipelines" />
                                        <span>Name</span>
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger as-child>
                                                    <Button
                                                        v-can-access.permissions.and="[$Permissions.WRITE_PIPELINES, $Permissions.READ_TAGS]"
                                                        variant="outline"
                                                        size="icon"
                                                        :disabled="!anyCheckedPipeline"
                                                        class="h-8 w-8"
                                                        @click="isBulkUpdateTagsModalOpen = true">
                                                        <Tag class="h-4 w-4" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent v-if="!anyCheckedPipeline">
                                                    <p>Select pipelines to update tags</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                </TableHead>
                                <TableHead
                                    v-can-access.permissions.and="[$Permissions.READ_TAGS, $Permissions.READ_TAGS]"
                                    colspan="3"
                                    :class="maxNumberOfTagsPerPipeline > 2 ? 'min-w-[15rem]' : ''">
                                    <div class="flex">
                                        <span>Tags</span>
                                    </div>
                                </TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Latency</TableHead>
                                <TableHead>Lag</TableHead>
                                <TableHead>Topics</TableHead>
                                <TableHead>Source</TableHead>
                                <TableHead>Destination</TableHead>
                                <TableHead class="last:rounded-tr-md"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <!-- Skeleton rows when loading -->
                            <template v-if="generalLoading">
                                <TableRow v-for="n in 10" :key="`skeleton-${n}`">
                                    <!-- Only show Name column skeleton if header would show it (which it won't during loading) -->
                                    <!-- <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell> -->
                                    <!-- Only show Tags skeleton if user has permissions -->
                                    <TableCell v-can-access.permissions.and="[$Permissions.READ_TAGS, $Permissions.READ_TAGS]" colspan="3">
                                        <SkeletonLoader preset="table-name" :loading="true" />
                                    </TableCell>
                                    <TableCell><SkeletonLoader preset="table-status" :loading="true" /></TableCell>
                                    <TableCell><SkeletonLoader preset="table-metric" :loading="true" /></TableCell>
                                    <TableCell><SkeletonLoader preset="table-metric" :loading="true" /></TableCell>
                                    <TableCell><SkeletonLoader preset="table-number" :loading="true" /></TableCell>
                                    <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                                    <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                                    <TableCell><SkeletonLoader preset="table-action" :loading="true" /></TableCell>
                                </TableRow>
                            </template>
                            <!-- Actual data rows -->
                            <template v-else>
                                <TableRow v-for="(pipeline, pipelineIndex) in searchedPipelines" :key="pipelineIndex">
                                    <TableCell>
                                        <div class="flex items-center gap-3">
                                            <Checkbox
                                                v-can-access.permissions.and="[$Permissions.WRITE_PIPELINES, $Permissions.READ_TAGS]"
                                                :checked="!!checkedPipeline[pipeline.id]"
                                                @update:modelValue="(checked) => checkPipeline(pipeline.id, checked)" />
                                            <Button
                                                variant="link"
                                                class="h-auto p-0 max-w-full text-left justify-start font-medium"
                                                :title="pipeline.name"
                                                @click="onPipelineSelected(pipeline)">
                                                <span
                                                    class="truncate max-w-[200px] sm:max-w-[250px] md:max-w-[300px] lg:max-w-[350px] xl:max-w-[400px]">
                                                    {{ pipeline.name }}
                                                </span>
                                            </Button>
                                        </div>
                                    </TableCell>
                                    <TableCell v-can-access.permissions="[$Permissions.READ_TAGS]" colspan="3">
                                        <div class="flex flex-col mr-4 gap-1 w-full">
                                            <div v-if="pipelineTags?.length && pipeline.tags?.length" class="flex flex-wrap gap-1">
                                                <Badge
                                                    v-for="(tag, index) in visibleTags(pipeline)"
                                                    :key="tag.id || tag._id"
                                                    variant="outline"
                                                    :class="`text-[0.6rem] ${TAG_BADGE_COLOR_CLASSES_PER_TYPE[tag.type[0]?.toLowerCase()] || TAG_COLORS_PER_NAME[tag.name?.toLowerCase()]}`">
                                                    {{ tag.name }}
                                                </Badge>

                                                <Button
                                                    v-if="pipelineTagsToShowMore(pipeline)"
                                                    variant="link"
                                                    size="sm"
                                                    class="h-auto p-0 text-xs font-medium"
                                                    @click="toggleShowMoreTags(pipeline.id)">
                                                    {{
                                                        showMoreTags[pipeline.id]
                                                            ? 'Hide'
                                                            : `Show ${allPipelineTags(pipeline)?.length - maxTagsToShow} more...`
                                                    }}
                                                </Button>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <SkeletonLoader preset="inline-metric" :loading="metricsLoading || itemLoading[pipeline.id]">
                                            <Badge
                                                :variant="
                                                    getBadgeVariantAndClasses(allLatestMetrics[pipeline.id]?.inline.status ?? 'Unknown').variant
                                                "
                                                :class="getBadgeVariantAndClasses(allLatestMetrics[pipeline.id]?.inline.status ?? 'Unknown').classes"
                                                @click="allLatestMetrics[pipeline.id]?.inline.status ? onPipelineStatusClick(pipeline) : null">
                                                {{ allLatestMetrics[pipeline.id]?.inline.status ?? '-' }}
                                                <Info v-if="allLatestMetrics[pipeline.id]?.inline.status === 'Broken'" class="h-4 w-4 ml-1" />
                                            </Badge>
                                        </SkeletonLoader>
                                    </TableCell>
                                    <TableCell
                                        :variant="
                                            metricsLoading ||
                                            itemLoading[pipeline.id] ||
                                            getFormattedValue(pipeline, 'latency', 'milliseconds').display === '-'
                                                ? 'default'
                                                : 'metric-badge'
                                        ">
                                        <SkeletonLoader v-if="metricsLoading || itemLoading[pipeline.id]" preset="table-status" :loading="true" />
                                        <template v-else>
                                            <div class="flex items-center">
                                                {{ getFormattedValue(pipeline, 'latency', 'milliseconds').display }}
                                                <small v-if="allLatestMetrics[pipeline.id]?.inline.latency > 0" class="flex items-center">
                                                    <small v-if="allLatestMetrics[pipeline.id]?.inline.latency >= 60000" class="ml-1">
                                                        <Clock class="h-4 ml-1 w-4 shrink-0 text-primary" />
                                                    </small>
                                                </small>
                                            </div>
                                        </template>
                                    </TableCell>
                                    <TableCell
                                        :variant="
                                            metricsLoading ||
                                            itemLoading[pipeline.id] ||
                                            getFormattedValue(pipeline, 'recordsLag', 'count').display === '-'
                                                ? 'default'
                                                : 'metric-badge'
                                        ">
                                        <SkeletonLoader v-if="metricsLoading || itemLoading[pipeline.id]" preset="inline-small" :loading="true" />
                                        <template v-else>
                                            {{ getFormattedValue(pipeline, 'recordsLag', 'count').display }}
                                        </template>
                                    </TableCell>
                                    <TableCell>
                                        <SkeletonLoader preset="table-action" :loading="metricsLoading || itemLoading[pipeline.id]">
                                            {{ pipeline.topics.length }}
                                        </SkeletonLoader>
                                    </TableCell>
                                    <TableCell>
                                        <div class="flex items-center mr-4 gap-2">
                                            <Avatar class="h-6 w-6 bg-background dark:bg-slate-200 border border-border rounded-sm shrink-0">
                                                <AvatarImage
                                                    :src="getConnectorIcon(pipeline.source.connector) || `/icons/${pipeline.source.connector}.svg`"
                                                    :alt="pipeline.source.connector"
                                                    class="object-contain p-0.5" />
                                                <AvatarFallback
                                                    class="bg-background dark:bg-slate-200 dark:text-slate-800 text-muted-foreground text-xs rounded-sm">
                                                    {{ pipeline.source.connector?.charAt(0).toUpperCase() }}
                                                </AvatarFallback>
                                            </Avatar>
                                            <Button
                                                variant="link"
                                                class="h-auto p-0 text-xs font-normal text-left justify-start !text-primary"
                                                :title="pipeline.source.name"
                                                @click="router.push(selectedConnectorPath(pipeline.source.id, 'source'))">
                                                <span
                                                    class="truncate max-w-[120px] sm:max-w-[140px] md:max-w-[160px] lg:max-w-[180px] xl:max-w-[200px]">
                                                    {{ pipeline.source.name }}
                                                </span>
                                            </Button>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div class="flex items-center mr-4 gap-2">
                                            <Avatar class="h-6 w-6 bg-background dark:bg-slate-200 border border-border rounded-sm shrink-0">
                                                <AvatarImage
                                                    :src="
                                                        getConnectorIcon(pipeline.destination.connector) ||
                                                        `/icons/${pipeline.destination.connector}.svg`
                                                    "
                                                    :alt="pipeline.destination.connector"
                                                    class="object-contain p-0.5" />
                                                <AvatarFallback
                                                    class="bg-background dark:bg-slate-200 dark:text-slate-800 text-muted-foreground text-xs rounded-sm">
                                                    {{ pipeline.destination.connector?.charAt(0).toUpperCase() }}
                                                </AvatarFallback>
                                            </Avatar>
                                            <Button
                                                variant="link"
                                                class="h-auto p-0 text-xs font-normal text-left justify-start"
                                                :title="pipeline.destination.name"
                                                @click="router.push(selectedConnectorPath(pipeline.destination.id, 'destination'))">
                                                <span
                                                    class="truncate max-w-[120px] sm:max-w-[140px] md:max-w-[160px] lg:max-w-[180px] xl:max-w-[200px] !text-primary">
                                                    {{ pipeline.destination.name }}
                                                </span>
                                            </Button>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <ActionMenu
                                            variant="menu"
                                            :show-edit="userHasAccess([$Permissions.WRITE_PIPELINES], authStore.user)"
                                            :show-logs="
                                                userHasAccess(
                                                    [$Permissions.READ_SOURCE_METRICS, $Permissions.READ_DESTINATION_METRICS],
                                                    authStore.user
                                                )
                                            "
                                            :show-tags-update="userHasAccess([$Permissions.WRITE_PIPELINES, $Permissions.READ_TAGS], authStore.user)"
                                            :show-view="true"
                                            :show-delete="userHasAccess([$Permissions.DELETE_PIPELINES], authStore.user)"
                                            :show-stop-source="
                                                userHasAccess([$Permissions.WRITE_SOURCES], authStore.user) && showStopSource(pipeline)
                                            "
                                            :show-stop-destination="
                                                userHasAccess([$Permissions.WRITE_DESTINATIONS], authStore.user) && showStopDestination(pipeline)
                                            "
                                            :show-resume-source="
                                                userHasAccess([$Permissions.WRITE_SOURCES], authStore.user) && showResumeSource(pipeline)
                                            "
                                            :show-resume-destination="
                                                userHasAccess([$Permissions.WRITE_DESTINATIONS], authStore.user) && showResumeDestination(pipeline)
                                            "
                                            :show-snapshot="
                                                userHasAccess([$Permissions.TRIGGER_ACTION_SOURCES], authStore.user) && canPipelineSnapshot(pipeline)
                                            "
                                            :show-cancel-snapshot="
                                                userHasAccess([$Permissions.TRIGGER_ACTION_SOURCES], authStore.user) &&
                                                canPipelineCancelSnapshot(pipeline)
                                            "
                                            :loading="actionLoading[pipeline.id]"
                                            @stopDestination="onStopDestination(pipeline)"
                                            @resumeDestination="onResumeDestination(pipeline)"
                                            @stopSource="onStopSource(pipeline)"
                                            @resumeSource="onResumeSource(pipeline)"
                                            @restart="onRestart(pipeline.id)"
                                            @updateTags="onUpdateTags(pipeline)"
                                            @edit="onPipelineSelected(pipeline)"
                                            @view="onPipelineViewed(pipeline.id)"
                                            :show-copy-id="true"
                                            @snapshot="onSnapshot(pipeline)"
                                            @cancelSnapshot="onCancelSnapshot(pipeline)"
                                            @delete="onDelete('pipeline', pipeline.name, pipeline.id)"
                                            @logs="onViewLogs(pipeline)"
                                            @copyId="onCopyId(pipeline.id)" />
                                    </TableCell>
                                </TableRow>
                                <TableEmpty v-if="!generalLoading && pipelinesStore.all !== undefined && !searchedPipelines.length" :colspan="11">
                                    <TableEmptyState />
                                </TableEmpty>
                            </template>
                        </TableBody>
                    </Table>
                    <ScrollBar orientation="horizontal" />
                    <ScrollBar orientation="vertical" />
                </ScrollArea>

                <TablePagination
                    v-if="pipelinesStore.pipelines?.length || page > 0"
                    :current-page="page"
                    :page-size="pageSize"
                    :total-items="pipelinesStore.total"
                    :current-page-items-count="pipelinesStore.pipelines?.length || 0"
                    :filtered-count="searchedPipelines.length !== pipelinesStore.pipelines?.length ? searchedPipelines.length : null"
                    item-type="pipelines"
                    :loading="generalLoading"
                    @page-change="onPageChange"
                    @page-size-change="onPipelinesPerPageChange" />
            </div>
        </div>
    </div>
</template>
